import { useState, useMemo } from 'react';
import Map from '@rio-cloud/rio-uikit/Map';
import MarkerLayer from '@rio-cloud/rio-uikit/MarkerLayer';
import EventUtils from '@rio-cloud/rio-uikit/EventUtils';
import MapSettings from '@rio-cloud/rio-uikit/MapSettings';
import MapTypeSettings from '@rio-cloud/rio-uikit/MapTypeSettings';
import MapClusterSettings from '@rio-cloud/rio-uikit/MapClusterSettings';
import SingleMapMarker from '@rio-cloud/rio-uikit/SingleMapMarker';
import ClusterMapMarker from '@rio-cloud/rio-uikit/ClusterMapMarker';
import type { MapEvent, Position } from '@rio-cloud/rio-uikit/mapTypes';
import { ISandboxMarker } from 'src/components/Map';
import { useMapDrawingContext } from 'src/providers/MapDrawingContext';
import Marker from '@rio-cloud/rio-uikit/Marker';

interface Props {
    position: Position;
    zoom: number;
    markers: ISandboxMarker[];
    showInterestPoints: boolean;
    handleSelect: (id: string) => void;
}

const MapCluster = ({ position, zoom, markers, showInterestPoints, handleSelect }: Props) => {
    const {
        interestPoints,
    } = useMapDrawingContext();

    const [showCluster, setShowCluster] = useState(true);
    const simpleTheme = useMemo(
        () => ({
            getNoisePresentation: ({ name, numberWarnings, numberExceptions, type }: any) => (
                <SingleMapMarker
                    iconNames={[type]}
                    name={name}
                    warningCount={numberWarnings}
                    exceptionCount={numberExceptions}
                />
            ),
            getClusterPresentation: (assets: any[]) => (
                <ClusterMapMarker
                    count={assets.length}
                    warningCount={0}
                    exceptionCount={0}
                />
            ),
        }),
        []
    );

    const handleClusterEvent = (event: MapEvent) => {
        const target = event.target.getData();
        event.stopPropagation();

        if (target.isCluster()) {
            // console.log('cluster event:', event.type);
            // target.forEachDataPoint((point: Point) => console.log(point.getData()));
        } else {
            // console.log('noise point event:', event.type, target.getData());
            handleSelect(target?.getData()?.data)
        }
    };

    const handleClusterChange = (newShowCluster: boolean) => setShowCluster(newShowCluster);

    const eventListenerMap = EventUtils.createTapOnDblTapPreventer({
        [EventUtils.TAP]: handleClusterEvent,
        [EventUtils.DBL_TAP]: handleClusterEvent,
    });

    return (
        <div className='width-100pct height-100pct'>
            <Map
                credentials={{
                    apikey: process.env.REACT_APP_RIO_MAP_KEY as string,
                }}
                center={position}
                zoom={zoom}
                showCluster={showCluster}
                onShowClusterChange={handleClusterChange}
                mapSettings={
                    <MapSettings
                        options={[
                            <MapTypeSettings
                                key='mapTypeSettings'
                                tooltip='Change map type'
                                dropdownHeaderText='Map views'
                                defaultTypeLabel='Default view'
                                truckTypeLabel='Truck view'
                                terrainTypeLabel='Terrain view'
                                satelliteTypeLabel='Satellite view'
                                nightTypeLabel='Night view'
                            />,
                            <MapClusterSettings
                                key='mapClusterSettings'
                                tooltip='Enable or disable marker cluster'
                                dropdownHeaderText='Vehicle cluster'
                                clusterEnabledLabel='Enable cluster'
                                clusterDisabledLabel='Disable cluster'
                            />,
                        ]}
                    />
                }
            >
                <MarkerLayer data={markers?.map((res) => {
                    return {
                        lat: res?.position?.lat,
                        lng: res?.position?.lng,
                        data: res?.id,
                        name: res?.markerProps?.name,
                        type: res?.type
                    }
                })} eventListenerMap={eventListenerMap} simpleTheme={simpleTheme} />
                {showInterestPoints
                    ? interestPoints
                        .filter((point) => {
                            if (markers.length > 0) {
                                const editingMarker = markers[0];
                                const isSamePosition =
                                    Math.abs(point.latitude - editingMarker.position.lat) < 0.0001 &&
                                    Math.abs(point.longitude - editingMarker.position.lng) < 0.0001;
                                return !isSamePosition;
                            }
                            return true;
                        })
                        .map((point, index) => (
                            <Marker
                                key={`interest-point-${point.id ?? index}`}
                                position={{ lat: point.latitude, lng: point.longitude }}
                                icon={
                                    <SingleMapMarker
                                        name={point.nome || point.endereco || "Ponto de Interesse"}
                                        markerColor="bg-map-marker-poi"
                                        iconNames={["rioglyph rioglyph-map-marker"]}
                                        anchorSize="md"
                                    />
                                }
                                draggable={false}
                                eventListenerMap={{}}
                            />
                        ))
                    : null}
            </Map>
        </div>
    );
};

export default MapCluster;
